<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <div
    class="d-flex"
    style="overflow: visible"
  >
    <v-navigation-drawer
      :mini-variant="shouldDisplayMini"
      permanent
      style="position: relative;z-index: 3"
      touchless
      width="300"
    >
      <div
        :class="shouldDisplayMini?'py-2':'pa-4'"
        class="d-flex flex-column align-center"
        style="height: 100vh"
        @mouseenter="mouseEnter"
        @mouseleave="dialogOpen=!!activeItem"
      >
        <div
          :class="shouldDisplayMini?'text-center':'pl-2'"
          class="text-h3  mb-4 font-weight-black white--text"
          style="width: 100%;"
        >
          <v-icon>mdi-menu</v-icon>
        </div>
        <v-sheet
          v-for="categories in displayNavItem"
          :key="categories.title"
          :class="shouldDisplayMini?'justify-center':''"
          :color="navItemColor(categories?.group)"
          :rounded="!shouldDisplayMini"
          :tile="shouldDisplayMini"
          class="hoverFx text-body-2 font-weight-black d-flex align-center pa-2 mb-2"
          width="100%"
          @click="activeCurrentNavBarItem(categories,false)"
        >
          <div
            :class="shouldDisplayMini?'':'mr-4'"
          >
            <v-icon :color="routeIsActive(categories?.group)?'black':''">
              {{ categories.icon }}
            </v-icon>
          </div>
          <template v-if="!shouldDisplayMini">
            <div class="text-no-wrap">
              {{ $t(categories.title) }}
            </div>
            <v-spacer />
            <v-icon
              v-if="categories?.children?.length > 1"
              :color="routeIsActive(categories?.group)?'black':''"
            >
              mdi-chevron-right
            </v-icon>
          </template>
        </v-sheet>
        <v-spacer />
        <v-sheet
          v-if="!shouldDisplayMini"
          color="transparent"
          width="100%"
        >
          <div class="d-flex">
            <logo-display />
            <v-spacer />
            <language-switcher
              :active="changeLanguage"
              :current-locale-code="currentLocaleCode"
              :locales="$i18n.messages"
              @language-change="changeLanguage"
            />
          </div>
        </v-sheet>
        <template v-else>
          <v-sheet
            class="py-2 px-1 mb-2 mt-4 d-flex align-center justify-center"
            color="transparent"
            width="100%"
          >
            <v-img
              :aspect-ratio="1"
              :src="require('@/assets/new/logo/logo.png')"
              max-height="36px"
              max-width="36px"
            />
          </v-sheet>
        </template>
      </div>
    </v-navigation-drawer>
    <template v-if="$vuetify.breakpoint.lgAndUp">
      <navigation-list
        :active-item="activeItem"
        :dialog-open="dialogOpen"
        :item-loading="itemLoading"
        @goto="goto"
      />
    </template>
    <template v-else>
      <v-sheet
        v-if="dialogOpen"
        id="navContainer"
        dark
      >
        <navigation-list
          :active-item="activeItem"
          :dialog-open="dialogOpen"
          :item-loading="itemLoading"
          @goto="goto"
        />
      </v-sheet>
      <v-overlay
        :value="dialogOpen"
        style="width: 100vw;height: 100vh"
        z-index="2"
        @click="dialogOpen=false"
      />
    </template>

    <template v-if="loading" />
    <template v-else>
      <template v-if="haveNetworkError">
        <navigation-item
          :locked="false"
          :title="$t('ConnectionNotEstablished')"
          icon="mdi-access-point-network-off"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { productActivatePage, productIsReadyForStore, restaurantMenu } from '@/views/AppIndex/navigation'
import { mapMutations } from 'vuex'
import NavigationItem from '@/views/AppIndex/Widget/NavigationItem.vue'
import { getActiveProductCode } from '@/model/dataLayer/api/SaaS/user'
import GlobalConfig, { changeLanguage, Remember, requireOfflineMode } from '@/model/global/GlobalSettings'
import { getCurrentAuthPower } from '@/model/dataLayer/api/tableAndPerson/ServantManage'
import LogoDisplay from '@/views/widget/uiComponent/LogoDisplay.vue'
import NavigationList from '@/views/AppIndex/NavigationList.vue'
import store from '@/model/global/store'
import LanguageSwitcher from '@/views/widget/commonLogic/LanguageSwitcher.vue'

export default {
  name: 'NavigationItems',
  components: {
    LanguageSwitcher,
    NavigationList,
    LogoDisplay,
    NavigationItem
  },
  props: {
    offlineMode: {},
    isDarkMode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialogOpen: false,
      actionLock: false,
      navItems: restaurantMenu,
      loading: true,
      activeItem: null,
      itemLoading: false,
      haveNetworkError: false
    }
  },
  computed: {
    currentLocaleCode () {
      return this.$i18n.locale
    },
    displayNavItem () {
      return this.navItems.filter(it => !it.needWifi || !this.offlineMode)
    },
    shouldDisplayMini () {
      return this.dialogOpen || this.$vuetify.breakpoint.mdOnly
    }
  },
  watch: {},
  async mounted () {
    await this.initialize()
  },
  methods: {
    changeLanguage (locale) {
      Remember.locale = locale
      this.$i18n.locale = Remember.locale
      changeLanguage(locale)
      location.reload()
    },
    itemLoadingTime (ms) {
      setTimeout(() => {
        this.itemLoading = false
      }, ms)
    },
    ...mapMutations(['showProductDetail', 'showInternetErrorDialog', 'showErrorDialog']),
    async initialize () {
      if (!requireOfflineMode()) {
        if (GlobalConfig.blType !== 'Shop') {
          await this.checkBlType()
        }
        try {
          const allActiveProduct = await getActiveProductCode()
          await this.checkProductLocked(this.navItems, allActiveProduct)
        } catch (e) {
          console.log('network status', e)
          console.error(e)
          this.haveNetworkError = true
        }
      }
      const currentAuth = await getCurrentAuthPower()
      await this.checkPowerLocked(this.navItems.map(it => it.children).flat(), currentAuth)
      if (store.state.adminMode === 'supermarket') {
        await this.checkSuperMarketType()
      }
      if (GlobalConfig.mode !== 'Admin') {
        await this.checkAdminMode()
      }
      this.loading = false
    },
    async checkSuperMarketType () {
      this.navItems = this.navItems.filter(it => !it.notSupermarket).map(x => {
        x.children = x.children.filter(a => !a.notSupermarket)
        return x
      })
    },
    async checkBlType () {
      this.navItems = this.navItems.filter(it => it.title === 'vipManage')
    },
    async checkAdminMode () {
      this.navItems = this.navItems.filter(it => !it.admin)
    },
    async checkPowerLocked (arr, currentAuth) {
      for (const n of arr) {
        n.noPower = !currentAuth.includes(n.title)
        if (n.children) {
          await this.checkPowerLocked(n.children, currentAuth)
        }
      }
    },
    navItemColor (group) {
      if (this.routeIsActive(group)) {
        return 'amber lighten-4 black--text'
      } else if (this.activeItem?.group === group) {
        return 'grey darken-2'
      } else {
        return 'transparent'
      }
    },
    routeIsActive (group) {
      return this.$route.path.includes(group)
    },
    async checkProductLocked (arr, activeProducts) {
      for (const n of arr) {
        if (!requireOfflineMode() && n.title === 'marketingStatistics') {
          n.locked = true
        } else {
          n.locked = n.authCode ? !activeProducts.includes(n.authCode) : false
        }
        if (n.children) {
          await this.checkProductLocked(n.children, activeProducts)
        }
      }
    },
    mouseEnter (e) {
      if (!this.actionLock) {
        this.dialogOpen = false
      }
    },
    activeCurrentNavBarItem (category, mouseMoveOn) {
      this.actionLock = true
      setTimeout(() => {
        this.actionLock = false
      }, 30)
      this.itemLoading = true
      this.itemLoadingTime(200)
      if (category.needWifi && requireOfflineMode()) {
        this.showInternetErrorDialog()
        this.itemLoading = false
        return
      }
      if (category.children.length === 1) {
        this.dialogOpen = false
        this.activeItem = null
        this.goto(category.children[0], category.group)
        return
      }

      if (this.activeItem?.title === category.title) {
        if (this.dialogOpen) {
          this.dialogOpen = false
          this.activeItem = null
          return
        } else {
          this.dialogOpen = true
          return
        }
      }
      if (!category.locked) {
        this.dialogOpen = false
        this.activeItem = null
        this.activeItem = category
        this.dialogOpen = true
      } else {
        if (!mouseMoveOn) {
          if (category.authCode && !!requireOfflineMode()) {
            this.showProductDetail({ code: category.authCode })
          } else {
            this.showInternetErrorDialog()
          }
        }
        if (!category.children) {
          this.dialogOpen = false
        }
      }
    },
    async goto (item, groupPath) {
      item.loading = true
      if (item.locked) {
        if (!requireOfflineMode()) {
          this.showProductDetail({ code: item.authCode })
        } else {
          this.showInternetErrorDialog()
        }
        item.loading = false
      } else {
        if (requireOfflineMode()) {
          if (item.needWifi) {
            this.showInternetErrorDialog()
            item.loading = false
            return
          }
        }
        let path = [groupPath, item.to].join('/')
        const ready = item.readyCode ? await productIsReadyForStore(item.readyCode) : true
        if (!ready) {
          path = productActivatePage(item.readyCode)
        }
        item.loading = false
        if (path) {
          if (this.$route.path !== path) {
            await (this.$router.push(path))
            if (this.$vuetify.breakpoint.mobile) {
              this.$nextTick(() => {
                this.dialogOpen = false
              })
            }
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">

#navContainer {
  top: 0px;
  position: fixed;
  width: 300px;
  left: 56px;
  height: 100vh;
  border-radius: 0 !important;
  z-index: 1001 !important;
  transition-duration: .4s !important;
}

#navContainer:hover {

  box-shadow: 15px 5px 15px -24px !important;
}

#navContainer .v-list-item-group {
  opacity: 0;
}

.hoverFx {
  cursor: pointer;
  transition: .1s ease-in-out;
}

.hoverFx:hover {
  background: #fff1 !important;
}

.listItemStyle .v-list-item {
  padding-right: 16px !important;
  padding-left: 16px !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  color: #001d35;
}

.listItemStyle .v-list-item:before {
  border-radius: 9999px !important;
}

.listItemStyle .v-list-item:before :hover {
  background: #1111 !important;
  opacity: 1;
}

</style>
